import { useId, useRef, useState } from 'react';

import { ReactComponent as FileIcon } from '../../../../../assets/icons/file-icon.svg';
import { ReactComponent as UploadIcon } from '../../../../../assets/icons/upload.svg';

import './FileDropzone.scss';

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

export interface FileDropzoneProps {
  file: File | null;
  onFileSelect: (file: File) => void;
  onClear: () => void;
  accept?: string;
  label?: string;
  orLabel?: string;
  browseLabel?: string;
  caption?: string;
}

export default function FileDropzone({
  file,
  onFileSelect,
  onClear,
  accept = ACCEPTED_EXTENSIONS.join(','),
  label = 'Drop your file here',
  orLabel = 'or',
  browseLabel = 'browse files',
  caption
}: Readonly<FileDropzoneProps>) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const acceptFile = (incoming: File) => {
    onFileSelect(incoming);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (picked) acceptFile(picked);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) acceptFile(dropped);
  };

  const dropzoneClass = [
    'file-dropzone-style',
    dragOver ? 'file-dropzone-style--drag-over' : '',
    file ? 'file-dropzone-style--has-file' : ''
  ]
    .filter(Boolean)
    .join(' ');

  if (file) {
    return (
      <section
        aria-label="Selected file"
        className={dropzoneClass}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <div className="file-dropzone-style__file-row">
          <div className="file-dropzone-style__file-info">
            <FileIcon />
            <div>
              <div className="file-dropzone-style__file-name">{file.name}</div>
              <div className="file-dropzone-style__file-size">{Math.round(file.size / 1024)} KB</div>
            </div>
          </div>
          <button
            type="button"
            className="file-dropzone-style__remove"
            aria-label="Remove file"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
          >
            ×
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="file-dropzone-style__input"
        onChange={handleInputChange}
        aria-label={label}
        data-testid="file-upload-input"
      />
      <button
        type="button"
        className={dropzoneClass}
        aria-label={label}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{ width: '100%', fontFamily: 'inherit', color: 'inherit' }}
      >
        <span className="file-dropzone-style__icon">
          <UploadIcon />
        </span>
        <div>
          <div className="file-dropzone-style__label">{label}</div>
          <div className="file-dropzone-style__hint">
            {orLabel} <span className="file-dropzone-style__hint-strong">{browseLabel}</span>
          </div>
        </div>
        {caption && <div className="file-dropzone-style__caption">{caption}</div>}
      </button>
    </>
  );
}
